// Lazy resource evaluation (03 §4.1, 02 §2.2). All amounts and rates are integer milli-units.
//
// Accrual over [from, to] is floor(rate × to / H) − floor(rate × from / H) on absolute epoch time.
// Consecutive intervals telescope, so evaluating every second for an hour yields exactly the same
// amount as one evaluation over the hour: sub-milli fractions are never lost while a rate is
// constant. A rate change (building completion, capture, …) settles first, then starts a new series.
import type { Balance, FacilityType, GalaxySpeed, ResourceType } from '@stars-and-ships/balance';
import { MS_PER_HOUR } from '@stars-and-ships/balance';
import { MILLI, type PlanetCounts, planetMultiplierPct, type ResourcesMilli } from './stockpile.js';

export type FacilityLevels = Partial<Record<FacilityType, number>>;

export interface ResourceEconomy {
  /** Milli-units per hour, already scaled by the galaxy speed (G-40). */
  ratesPerH: ResourcesMilli;
  caps: ResourcesMilli;
}

const H = BigInt(MS_PER_HOUR);

export const PRODUCTION_FACILITY: Readonly<Record<ResourceType, FacilityType>> = {
  metal: 'metal_mine',
  gas: 'gas_harvester',
  supplies: 'supply_farm',
};

export const STORAGE_FACILITY: Readonly<Record<ResourceType, FacilityType>> = {
  metal: 'metal_storage',
  gas: 'gas_storage',
  supplies: 'supply_storage',
};

const PLANETS: Readonly<Record<ResourceType, keyof PlanetCounts>> = {
  metal: 'orePlanets',
  gas: 'gasGiants',
  supplies: 'earthLikes',
};

const RESOURCES = ['metal', 'gas', 'supplies'] as const;

const mapResources = (fn: (r: ResourceType) => bigint): ResourcesMilli => ({
  metal: fn('metal'),
  gas: fn('gas'),
  supplies: fn('supplies'),
});

const level = (facilities: FacilityLevels, facility: FacilityType) => facilities[facility] ?? 0;

const tableValue = (table: readonly number[], index: number, name: string): number => {
  const value = table[index];
  if (value === undefined) throw new RangeError(`${name} has no entry for level ${index}`);
  return value;
};

/**
 * An owned system (03 §4.1, slice: no research, boost or Citadel factors): production from the mines
 * × planet bonus × galaxy speed, caps from the storage levels.
 */
export function ownedSystemEconomy(
  balance: Balance,
  planets: PlanetCounts,
  facilities: FacilityLevels,
  speed: GalaxySpeed
): ResourceEconomy {
  const { production_per_h, storage_cap } = balance.economy;
  return {
    ratesPerH: mapResources((r) => {
      const base = tableValue(
        production_per_h,
        level(facilities, PRODUCTION_FACILITY[r]),
        'production_per_h'
      );
      const pct = planetMultiplierPct(balance, planets[PLANETS[r]]);
      return speed.ratePerH((BigInt(base) * MILLI * BigInt(pct)) / 100n);
    }),
    caps: mapResources(
      (r) =>
        BigInt(tableValue(storage_cap, level(facilities, STORAGE_FACILITY[r]), 'storage_cap')) *
        MILLI
    ),
  };
}

/** The share of each cap a raid cannot take (`economy.raiding.protected_pct_of_storage_cap`). */
export function protectedAmounts(balance: Balance, caps: ResourcesMilli): ResourcesMilli {
  const pct = BigInt(balance.economy.raiding.protected_pct_of_storage_cap);
  return mapResources((r) => (caps[r] * pct) / 100n);
}

function accrued(ratePerH: bigint, fromMs: number, toMs: number): bigint {
  return (ratePerH * BigInt(toMs)) / H - (ratePerH * BigInt(fromMs)) / H;
}

/**
 * Stored amounts at `toMs`. Amounts already at or above their cap stay where they are (nothing is
 * taken away); others grow up to the cap. Evaluating at or before `fromMs` changes nothing.
 */
export function evaluateResources(
  amounts: ResourcesMilli,
  economy: ResourceEconomy,
  fromMs: number,
  toMs: number
): ResourcesMilli {
  if (toMs <= fromMs) return amounts;
  return mapResources((r) => {
    const current = amounts[r];
    const cap = economy.caps[r];
    if (current >= cap) return current;
    const next = current + accrued(economy.ratesPerH[r], fromMs, toMs);
    return next > cap ? cap : next;
  });
}

/** Adds resources up to the caps (refunds, loot). Returns the new amounts and what was really added. */
export function addCapped(
  amounts: ResourcesMilli,
  add: ResourcesMilli,
  caps: ResourcesMilli
): { amounts: ResourcesMilli; added: ResourcesMilli } {
  const next = mapResources((r) => {
    if (amounts[r] >= caps[r]) return amounts[r];
    const sum = amounts[r] + add[r];
    return sum > caps[r] ? caps[r] : sum;
  });
  return { amounts: next, added: mapResources((r) => next[r] - amounts[r]) };
}

/** `pct` of each amount, rounded down. */
export function percentOf(amounts: ResourcesMilli, pct: number): ResourcesMilli {
  const p = BigInt(pct);
  return mapResources((r) => (amounts[r] * p) / 100n);
}

/** Resources missing to pay `cost` (whole milli-units; zero where there is enough). */
export function missingResources(amounts: ResourcesMilli, cost: ResourcesMilli): ResourcesMilli {
  return mapResources((r) => (cost[r] > amounts[r] ? cost[r] - amounts[r] : 0n));
}

export const canAfford = (amounts: ResourcesMilli, cost: ResourcesMilli): boolean =>
  RESOURCES.every((r) => amounts[r] >= cost[r]);

export const subtractResources = (a: ResourcesMilli, b: ResourcesMilli): ResourcesMilli =>
  mapResources((r) => a[r] - b[r]);

export const ZERO_RESOURCES: ResourcesMilli = Object.freeze({ metal: 0n, gas: 0n, supplies: 0n });

/** Whole units for the API (04 §2.2): floor(milli / 1000). */
export function toWholeUnits(amounts: ResourcesMilli): {
  metal: number;
  gas: number;
  supplies: number;
} {
  return {
    metal: Number(amounts.metal / MILLI),
    gas: Number(amounts.gas / MILLI),
    supplies: Number(amounts.supplies / MILLI),
  };
}

export const fromWholeUnits = (r: { metal: number; gas: number; supplies: number }) =>
  mapResources((k) => BigInt(r[k]) * MILLI);
